var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useState, useEffect, useMemo } from 'react';
import AddressLookup from '../../AddressLookup/AddressLookup';
import FormErrorSummary from '../../FormErrorSummary/FormErrorSummary';
import useStyles from './PCCDeliveryAddress.styles';
import { addressFields, errorMessages, getCountryFromAlpha2Code, SiteContext, addressValidationRegEx, commonValidationSchemas, createCountyValidation, isCountyRequired, } from '../../../utils/common';
import { FormikContext } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import Link from '../../Link/Link';
import { useFeatureFlag } from '../../../utils/useExperiment';
import { enable_address_lookup_eav_version } from '../../../utils/featureFlags';
export const validationAddressSchema = Yup.object({
    address: Yup.string()
        .min(4, errorMessages(4).minChars)
        .required(errorMessages('Address').required)
        .matches(addressValidationRegEx, errorMessages().unsupportedCharacters),
    addressLine1: commonValidationSchemas.addressLine1,
    addressLine2: commonValidationSchemas.addressLine2,
    city: commonValidationSchemas.city,
    county: createCountyValidation(),
    postalCode: commonValidationSchemas.postalCode,
    country: Yup.string().required(errorMessages('Country').required),
    isManualAddress: Yup.boolean(),
});
const PCCDeliveryAddress = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { currentStep, step, isOpen, setCurrentStep, setIsShowDayPicker, showInternationalLink, setShowInternationalLink, trackPostCodeCheckerAddress, trackPostCodeCheckerManualAddress, trackEnterManualAddressClick, locationInternationalDelivery, setIsManualAddressChoosen, hideOccasion, } = props;
    const { classes } = useStyles();
    const formikContext = useContext(FormikContext);
    const { getFormattedAddress, countryCode, shorthand, isFlyingFlowers } = useContext(SiteContext);
    const labelText = isFlyingFlowers ? 'Search for address' : 'Delivery address';
    const placeholderText = useMemo(() => {
        if (shorthand === 'ie')
            return 'Try the eircode or street name';
        else if (isFlyingFlowers)
            return 'Enter delivery postcode';
        else
            return 'Type postcode or full address';
    }, [isFlyingFlowers, shorthand]);
    const isExperianAperture = useFeatureFlag(enable_address_lookup_eav_version.key);
    const isManualAddressFormVisible = Boolean((_a = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _a === void 0 ? void 0 : _a.showManualAddress);
    const setManualAddressChosen = (isManualAddress) => {
        if (formikContext) {
            formikContext.setFieldValue('isManualAddress', isManualAddress);
            if (isManualAddress) {
                setIsManualAddressChoosen === null || setIsManualAddressChoosen === void 0 ? void 0 : setIsManualAddressChoosen(true);
                trackEnterManualAddressClick === null || trackEnterManualAddressClick === void 0 ? void 0 : trackEnterManualAddressClick('PCC');
            }
        }
    };
    let addressResponse;
    const countryDetails = getCountryFromAlpha2Code(countryCode);
    const [countyRequired, setCountyRequired] = useState(false);
    useEffect(() => {
        var _a;
        setCountyRequired(isCountyRequired(((_a = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _a === void 0 ? void 0 : _a.isManualAddress) || false, countryCode || ''));
    }, [(_b = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _b === void 0 ? void 0 : _b.isManualAddress, countryCode]);
    // IFCT-9175: keeps composite `address` field (required by validationAddressSchema) in sync with manual entry
    useEffect(() => {
        var _a;
        if (!((_a = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _a === void 0 ? void 0 : _a.isManualAddress))
            return;
        const { addressLine1, addressLine2, city, postalCode, address } = formikContext.values;
        const addressFieldList = [addressLine1, addressLine2, city, postalCode].filter(Boolean).join(', ');
        if (addressFieldList !== (address !== null && address !== void 0 ? address : '')) {
            void formikContext.setFieldValue('address', addressFieldList, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        (_c = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _c === void 0 ? void 0 : _c.isManualAddress,
        (_d = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _d === void 0 ? void 0 : _d.addressLine1,
        (_e = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _e === void 0 ? void 0 : _e.addressLine2,
        (_f = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _f === void 0 ? void 0 : _f.city,
        (_g = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _g === void 0 ? void 0 : _g.postalCode,
    ]);
    const handleAddressFormatCall = (suggestion, format) => __awaiter(void 0, void 0, void 0, function* () {
        if (!getFormattedAddress) {
            throw new Error('Format address callback not found in context');
        }
        else {
            const { data, error } = yield getFormattedAddress({
                input: {
                    suggestion,
                    format,
                    countryCode,
                    isExperianAperture,
                },
            });
            if (error) {
                throw new Error(`Address formatting returned an error response: ${error}`);
            }
            else if (!data) {
                throw new Error('No results returned from address formatting');
            }
            addressResponse = data.formatAddress;
            return data.formatAddress;
        }
    });
    const handleStep = () => {
        var _a, _b;
        if (hideOccasion) {
            // IFCT-8794: always advance to step 3; AutoSubmitHideOccasion handles submission
            setCurrentStep(3);
        }
        else if (formikContext.values.date && !formikContext.values.occasion) {
            setCurrentStep(3);
        }
        else if (formikContext.values.date && formikContext.values.occasion) {
            setCurrentStep(4);
        }
        else if (formikContext.values.date) {
            (_a = document === null || document === void 0 ? void 0 : document.querySelector('textarea')) === null || _a === void 0 ? void 0 : _a.blur();
        }
        else {
            setCurrentStep(1);
        }
        if ((_b = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _b === void 0 ? void 0 : _b.isManualAddress) {
            trackPostCodeCheckerManualAddress === null || trackPostCodeCheckerManualAddress === void 0 ? void 0 : trackPostCodeCheckerManualAddress();
        }
        else {
            trackPostCodeCheckerAddress === null || trackPostCodeCheckerAddress === void 0 ? void 0 : trackPostCodeCheckerAddress(addressResponse === null || addressResponse === void 0 ? void 0 : addressResponse.postalCode, addressResponse === null || addressResponse === void 0 ? void 0 : addressResponse.city, addressResponse === null || addressResponse === void 0 ? void 0 : addressResponse.county);
        }
        formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('isManualAddress', false);
        // Clear touched state so stale validation errors from Step 2 fields are not surfaced
        // by FormErrorSummary's showWhenTouched on subsequent steps.
        formikContext === null || formikContext === void 0 ? void 0 : formikContext.setTouched({}, false);
        setIsShowDayPicker(true);
    };
    const handleShowInternationalLink = () => {
        setShowInternationalLink === null || setShowInternationalLink === void 0 ? void 0 : setShowInternationalLink(true);
        locationInternationalDelivery === null || locationInternationalDelivery === void 0 ? void 0 : locationInternationalDelivery('PCC');
    };
    return (_jsx(_Fragment, { children: currentStep === step ? (_jsx("div", { className: clsx(((_h = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _h === void 0 ? void 0 : _h.isManualAddress) ? classes.manualAddressContainer : '', showInternationalLink ? classes.internationalContainer : '', step === currentStep && !hideOccasion && classes.activeStep), children: _jsxs("div", { className: classes.pccAddressContainer, children: [!isManualAddressFormVisible && (_jsx(FormErrorSummary, { fieldNames: ['address', 'addressLine1', 'addressLine2', 'city', 'county', 'postalCode', 'country'], waitForSubmitAfterMount: true, showWhenTouched: true })), _jsx(AddressLookup, { name: "address", label: labelText, placeholder: placeholderText, addressFields: addressFields, setManualAddressChosen: setManualAddressChosen, handleAddressFormatCall: handleAddressFormatCall, required: true, fullWidth: true, provideManualAddress: true, className: clsx(isOpen && classes.autoCompleteClass), boundariesElement: "scrollParent", countryCode: countryDetails === null || countryDetails === void 0 ? void 0 : countryDetails.codeAlpha3, countryList: [countryDetails], isFromPCC: true, setCurrentStep: handleStep, isCountyRequired: countyRequired }), !isFlyingFlowers ? (_jsx("div", { className: classes.internationalDelivery, children: _jsx(Link, { className: classes.internationalDeliveryLink, icon: "", label: "Do you want international delivery?", onClick: () => {
                                handleShowInternationalLink();
                            } }) })) : ('')] }) })) : null }));
};
export default PCCDeliveryAddress;
